import { ReactNode, useMemo } from "react"
import { Entity, Hit, noHit } from "./Entity.ts"
import fragmentSource from './Floor.fs?raw'
import vertexSource from './Floor.vs?raw'
import { useDrawCall } from "./hooks/useDrawCall.tsx"
import { useGL } from "./hooks/useGL.ts"
import { useLighting } from "./hooks/useLighting.ts"
import { useProgram } from "./hooks/useProgram.ts"
import { Ray } from "./VectorMath.ts"

const floorHeight: number = -5.0

export class Floor implements Entity {
  hitTest(ray: Ray): Hit {
    const t = (floorHeight - ray.origin.y) / (ray.direction.y)
    // This test is written to handle NaN correctly (as a miss).
    if (!(t > 0)) {
      return noHit
    }

    return {
      who: this,
      when: t,
      where: ray.at(t),
      extra: null,
    }
  }
}

export function FloorView(props: {
  floor: Floor
  enabled: boolean
}): ReactNode {
  const { enabled } = props

  const gl = useGL()
  const program = useProgram('Floor', gl, vertexSource, fragmentSource)
  const locations = useMemo(() => ({
    distanceFromOrigin: gl.getUniformLocation(program, 'u_distanceFromOrigin'),
    clipFromWorld: gl.getUniformLocation(program, 'u_clipFromWorld'),
  }), [gl, program])

  const runLighting = useLighting(gl, program)

  useDrawCall('Floor', ({ gl, clipFromWorld }) => {
    if (!enabled) {
      return
    }

    gl.useProgram(program)

    runLighting(() => {
      gl.uniform1f(locations.distanceFromOrigin, floorHeight)
      gl.uniformMatrix4fv(locations.clipFromWorld, false, Float32Array.from(clipFromWorld))

      gl.drawArrays(gl.TRIANGLE_FAN, 0, 6)

    })

    gl.useProgram(null)
  }, [gl, program, locations, enabled, runLighting])

  return undefined
}
